/**
 * Query-cache bookkeeping shared by every path that writes an appointment record.
 *
 * The calendar renders from the `weekAppointments*` query caches, not from the registry,
 * so a registry write is invisible until the matching cache entry is rebuilt. Both the
 * modal save and a drag & drop move need the same set of keys refreshed; keeping the list
 * in one place is what stops the two from drifting apart.
 */

import type { QueryClient } from "@tanstack/react-query";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import { dayReportKey } from "@/hooks/company/useDayReport";
import { externalBusyKey } from "@/hooks/calendar/useExternalBusy";
import { getWeekStart } from "@/lib/utils";
import { salonNow } from "@/lib/time";

/**
 * Rebuilds the cached week lists from current registry state.
 *
 * Used for optimistic moves: the registry is the single source of truth, so rather than
 * splicing a card between date buckets by hand, every affected list is re-derived from it.
 * That keeps a move across dates, across staff, or both, on one code path.
 */
export function resyncCalendarCaches(queryClient: QueryClient): void {
  const registry = getAppointmentRegistry();

  queryClient.setQueriesData<Record<string, any[]>>(
    { queryKey: ["weekAppointments"] },
    (old) => (old ? registry.formatAppointmentsForWeek(Object.keys(old)) : old)
  );

  for (const query of queryClient.getQueryCache().findAll({ queryKey: ["weekAppointmentsForStaff"] })) {
    const staffId = Number(query.queryKey[1]);
    const old = query.state.data as Record<string, any[]> | undefined;
    if (!old || !Number.isFinite(staffId)) continue;
    queryClient.setQueryData(
      query.queryKey,
      registry.formatAppointmentsForWeekForStaff(staffId, Object.keys(old))
    );
  }
}

/**
 * Invalidates everything a written record can appear in.
 *
 * `dates` and `staffIds` take both sides of a move: an appointment that leaves one day or
 * one staff member has to be refetched out of the list it came from as well as into the
 * one it landed in.
 */
export function invalidateRecordCaches(
  queryClient: QueryClient,
  params: { recordId?: string | number; dates?: (string | undefined)[]; staffIds?: (number | undefined)[] }
): void {
  const dates = Array.from(
    new Set((params.dates ?? []).filter((d): d is string => typeof d === "string" && !!d))
  );
  const staffIds = Array.from(
    new Set((params.staffIds ?? []).filter((id): id is number => Number.isFinite(id as number)))
  );

  for (const weekStart of new Set(dates.map((d) => getWeekStart(d)))) {
    queryClient.invalidateQueries({ queryKey: ["weekAppointments", weekStart] });
    // the week's busy bands refetch with its appointments (a 304 unless a block changed)
    queryClient.invalidateQueries({ queryKey: externalBusyKey(weekStart) });
  }

  for (const staffId of staffIds) {
    queryClient.invalidateQueries({ queryKey: ["weekAppointmentsForStaff", staffId] });
  }

  const reportDates = dates.length > 0 ? dates : [salonNow().toISODate()].filter(Boolean as any);
  for (const date of reportDates as string[]) {
    queryClient.invalidateQueries({ queryKey: dayReportKey(date) });
  }

  if (params.recordId != null) {
    queryClient.invalidateQueries({ queryKey: ["appointmentDetails", params.recordId] });
    queryClient.invalidateQueries({ queryKey: ["appointmentPaymentInfo", params.recordId] });
    // lines paid with a client package, and what each package has left
    queryClient.invalidateQueries({ queryKey: ["recordAbonements", Number(params.recordId)] });
  }
}
